import { isStaleRevisionError } from '@/utils/courses-encryption/annotation-revisions';
import { normaliseTagName } from '@/utils/courses-crypto/blind-index';
import { noteScopeOf } from '@/utils/courses-notes-corpus/schema';
import { resolveProgramName } from '@/utils/program-name';

/**
 * A member's personal Next Steps tags, as more than one surface has to talk
 * about them.
 *
 * Tags are MEMBER-WIDE (review 2026-09-10, ClickUp ONE-8528): one list across
 * Part 1, Part 2, the Student's Guide and the Leader's Guide, with names unique
 * per member compared case-insensitively. Notes are not — the Notes & Highlights
 * screen shows one part and one guide at a time. So the two things this module
 * owns both exist because the tag list is wider than the note list underneath it:
 *
 *   - the duplicate-name refusal, which the SERVER now makes far more often than
 *     the device can predict (the colliding tag may live in a part this screen
 *     never loaded, or be one this device cannot decrypt);
 *   - the wording of a usage breakdown, so "where else is this tag used" reads
 *     the same in the delete confirmation and in the empty filter state.
 */

/**
 * Whether a write failed because the member already has a tag of that name.
 *
 * `PATCH /api/tags/:id` answers 409 for a stale revision too, and the duplicate
 * check runs first — so a rename can be both. The revision refusal is the coded
 * one, and `isStaleRevisionError` already owns that half of the contract; this
 * is the rest of it, expressed against that rather than beside it.
 *
 * @param {unknown} error an axios failure from the annotations transport
 * @returns {boolean}
 */
export function isTagNameConflict(error) {
  return error?.response?.status === 409 && !isStaleRevisionError(error);
}

/**
 * Two tag names are the same name when these fold to the same string.
 *
 * `normaliseTagName` is the authority — it is what a blind index is computed
 * over, and since the API build (api#1158) the server folds a PLAINTEXT name the
 * same way, so one rule now covers both kinds of member. It throws by design, as
 * a guard on crypto input: on a name this device could not decrypt (`null`) and
 * on an empty string. This runs on every keystroke against every tag in the
 * list, where both are ordinary, so those two answer "matches nothing".
 *
 * @param {unknown} name
 * @returns {string} the folded name, or '' when there is nothing to fold
 */
export function foldTagName(name) {
  if (typeof name !== 'string' || !name.trim()) return '';
  return normaliseTagName(name);
}

/**
 * The one sentence a member is shown when the name they typed is already theirs.
 * Confirmed copy (review section 7, question 11) — the same wording everywhere,
 * whether the device spotted the duplicate or the server did.
 *
 * @param {string} name the name the member typed
 */
export function tagNameConflictMessage(name) {
  return `“${name}” already exists — select it below.`;
}

/** A tag-name conflict carries the attempted name, so every surface can say it. */
export class TagNameConflictError extends Error {
  constructor(name, cause) {
    super(tagNameConflictMessage(name));
    this.name = 'TagNameConflictError';
    this.tagName = name;
    this.cause = cause;
  }
}

/**
 * What to tell the member about a failed tag create. The `instanceof` check is
 * the one thing that moves if the error type ever changes, so it lives here
 * rather than in each of the three surfaces that can start a create.
 *
 * @param {unknown} error
 * @param {string} fallback what to say when it was not a duplicate ('' to say nothing)
 */
export function tagCreateErrorMessage(error, fallback = '') {
  return error instanceof TagNameConflictError ? error.message : fallback;
}

// The reader view a usage row belongs to. Through `noteScopeOf`, which already
// owns the STUDENT | LEADER → student | leader mapping for note rows; a usage
// row carries the same two fields, and one definition of that contract is
// enough.
const viewOf = row => noteScopeOf(row).view;

const guideLabel = view => (view === 'leader' ? 'the Leader’s Guide' : 'the Student’s Guide');

/**
 * What one bucket of a tag's usage is called.
 *
 * A bucket is a (part, guide) pair, because that is the scope one screen shows.
 * The guide half is only said to a LEADER: a student has one guide, and naming
 * it would be noise about a distinction they cannot see. The part's name comes
 * from `resolveProgramName`, so a prompt opened before the programs list has
 * landed reads "Next Steps Part 2" rather than "next-steps-p2".
 */
function bucketLabel({ programSlug, view }, { programs, isLeader }) {
  const part = programSlug ? resolveProgramName(programs, programSlug) : '';
  if (!isLeader) return part;
  return part ? `${part} (${guideLabel(view)})` : guideLabel(view);
}

/**
 * A tag's usage rows, collapsed into the labelled, ordered segments both the
 * delete confirmation and the empty filter state are built from.
 *
 * Buckets read in a fixed order so the sentence does not reshuffle between two
 * openings of the same confirmation: parts in the order the programs list gives
 * them, then by slug for any part that list does not know (which is every part
 * before it has loaded), and within a part the Student's Guide before the
 * Leader's Guide.
 *
 * @param {Array<{ programSlug?: string, audience?: string, count?: number }>} rows
 * @param {{ programs?: Array, isLeader?: boolean }} context
 * @returns {Array<{ label: string, count: number }>}
 */
export function tagUsageSegments(rows, { programs = [], isLeader = false } = {}) {
  const buckets = new Map();
  (rows || []).forEach(row => {
    const count = Number(row?.count) || 0;
    if (count <= 0) return;
    // A student never sees the guide half, so their two audiences fold into one
    // bucket rather than printing the same part name twice.
    const view = isLeader ? viewOf(row) : 'student';
    const key = `${row.programSlug || ''}:${view}`;
    const prior = buckets.get(key);
    if (prior) {
      prior.count += count;
      return;
    }
    const partIndex = programs.findIndex(p => p.slug === row.programSlug);
    buckets.set(key, {
      programSlug: row.programSlug,
      view,
      count,
      order: partIndex === -1 ? programs.length : partIndex,
    });
  });
  return [...buckets.values()]
    .sort(
      (a, b) =>
        a.order - b.order ||
        String(a.programSlug || '').localeCompare(String(b.programSlug || '')) ||
        Number(a.view === 'leader') - Number(b.view === 'leader'),
    )
    .map(bucket => ({ label: bucketLabel(bucket, { programs, isLeader }), count: bucket.count }));
}

/** How many notes carry a tag, across every part and guide. */
export function tagUsageTotal(rows) {
  return (rows || []).reduce((total, row) => total + (Number(row?.count) || 0), 0);
}

/** Whether a usage row falls outside the part and guide currently on screen. */
export function isElsewhere(row, { program, view } = {}) {
  const scope = noteScopeOf(row);
  return scope.program !== program || scope.view !== view;
}

/** "3 in Part 2" — one segment of a breakdown sentence. */
const segmentPhrase = ({ label, count }) => `${count} in ${label}`;

/** A usage breakdown as a single line: "2 in Part 2 · 1 in the Leader’s Guide". */
export function tagUsageBreakdown(segments) {
  return (segments || []).map(segmentPhrase).join(' · ');
}

/**
 * Why a tag filter came back empty: the notes it matches, in the parts and
 * guides this screen is not showing. Empty string when there are none — the
 * filter is then genuinely unmatched and the plain empty state is the truth.
 *
 * @param {Array<{ label: string, count: number }>} segments buckets outside the current scope
 * @param {number} tagCount how many tags the filter has selected
 */
export function tagFilterElsewhereCopy(segments, tagCount) {
  const list = segments || [];
  if (!list.length) return '';
  if (list.length === 1 && tagCount === 1) {
    const [{ label, count }] = list;
    return count === 1
      ? `1 note in ${label} carries this tag.`
      : `${count} notes in ${label} carry this tag.`;
  }
  const subject = tagCount > 1 ? 'these tags' : 'this tag';
  return `Notes carrying ${subject}: ${list.map(segmentPhrase).join(', ')}.`;
}
